import { Serie } from "@nivo/line";
import * as React from "react";
import { FlowVolumeGraph } from "./FlowVolumeGraph";
import { GraphTestData } from "./SpirometryAnalysisScreen";
import { VolumeTimeGraph } from "./VolumeTimeGraph";
import Alert, { AlertCategoryEnum } from "../../misc/components/Alert";
import { ErrorBoundary } from "../../errors/ErrorBoundary";
import { FvcSession, FvcTest } from "webportalclient";

interface SpirometryGraphsProps {
    data: GraphTestData[];
    expiratoryOnlyVisible: boolean;
    tZeroAdjust: boolean;
}

const toColorHex = (index: number) => {
    switch (index) {
        case 1:
            return "#F15854";
        case 2:
            return "#5DA5DA";
        case 3:
            return "#60BD68";
        case 4:
            return "#FAA43A";
        case 5:
            return "#F17CB0";
        case 6:
            return "#DECF3F";
        case 7:
            return "#B2912F";
        case 8:
            return "#B276B2";
        default:
            return "#000000";
    }
}

export const FvcGraphDataMapping = (session: FvcSession) => {
    const graphDataMapped = session.tests.map(t => {
        const volumePoints = t.volumeData.split(",").map(v => parseFloat(v));
        const flowPoints = t.flowData.split(",").map(f => parseFloat(f));

        const getTimeSamplePoints = (test: FvcTest) => {
            if (test.timeData) {
                return test.timeData.split(',').map(td => parseFloat(td));
            } else {
                return [...Array(volumePoints.length).keys()].map(i => t.sampleRate * i / 1000);
            }
        }

        const flowVolumePoints = volumePoints.map((x, i) => ({
            Volume: x,
            Flow: flowPoints[i],
            Time: getTimeSamplePoints(t)[i]
        }));

        return {
            ExpiratoryOnlyData: flowVolumePoints,
            TZeroAdjustedExpiratoryOnlyData: [],
            TZeroAdjustedData: [],
            TestId: t.id,
            TestNumber: t.index,
            ColorHex: toColorHex(t.index),
            IsDisplayed: true,
            VolumeData: flowVolumePoints
        } as GraphTestData
    });

    return graphDataMapped;
}

export const SpirometryGraphs = ({ data, expiratoryOnlyVisible, tZeroAdjust }: SpirometryGraphsProps) => {
    const volumeValues = data.flatMap(t => t.VolumeData.map(vd => vd.Volume));
    const flowValues = data.flatMap(t => t.VolumeData.map(vd => vd.Flow));

    const volumeMin = Math.min(...volumeValues);
    const volumeMax = Math.max(...volumeValues);
    const flowMin = Math.min(...flowValues);
    const flowMax = Math.max(...flowValues);

    const sortedData = data
        .filter(x => x.IsDisplayed)
        .sort((a, b) => b.TestNumber - a.TestNumber);    // HACK: This reverses the order of the items in the legend currently Nivo shows these the wrong way round

    const dataKey: keyof (GraphTestData) = expiratoryOnlyVisible ? (tZeroAdjust ? "TZeroAdjustedExpiratoryOnlyData" : "ExpiratoryOnlyData") : (tZeroAdjust ? "TZeroAdjustedData" : "VolumeData");

    const fVChartData: Serie[] = sortedData
        .map((d) => {
            return {
                id: `Test ${d.TestNumber}`,
                color: d.ColorHex,
                data: d[dataKey].map(da => ({
                    x: da.Volume,
                    y: da.Flow
                }))
            }
        });

    const vTChartData: Serie[] = sortedData
        .map(d => ({
            id: `Test ${d.TestNumber}`,
            color: d.ColorHex,
            data: d[dataKey].map(vd => ({
                x: vd.Time,
                y: vd.Volume
            }))
        }));

    if (fVChartData.length === 0 && vTChartData.length === 0)
        return <Alert show={true} category={AlertCategoryEnum.Danger}>There is no chart data to display. The Spirometry session may be incomplete.</Alert>

    const specificTimePeriod: number[] = vTChartData.flatMap(t => t.data.map(d => d.x)) as number[];
    const timeMin = Math.min(...specificTimePeriod);
    const timeMax = Math.max(...specificTimePeriod);

    if (sortedData.length === 0)
        return <Alert show={true} category={AlertCategoryEnum.Info}>There are no tests currently selected for display</Alert>

    return (
        <>
            <ErrorBoundary>
                <div id="FlowVolumeChartWrapper" className="col-md-6" style={{ height: 600 + 'px', minWidth: 915 + 'px' }}>
                    <h4>Flow / Volume Graph</h4>
                     <FlowVolumeGraph data={fVChartData} minVolume={volumeMin} maxVolume={volumeMax} minFlow={flowMin} maxFlow={flowMax} />
                </div>
                <div id="VolumeTimeChartWrapper" className="col-md-6" style={{ height: 600 + 'px', minWidth: 915 + 'px' }}>
                    <h4>Volume / Time Graph</h4>
                    <VolumeTimeGraph data={vTChartData} minVolume={volumeMin} maxVolume={volumeMax} minTime={timeMin} maxTime={timeMax} />
                </div>
            </ErrorBoundary>
        </>
    );
}
